/**
 * PATH: packages/api/src/view-preferences/errors.ts
 * FILE_ID: API-VIEWPREF-ERRORS-001
 * PURPOSE: Type durable preference concurrency and payload failures.
 * ROLE IN ARCHITECTURE: Framework-neutral errors crossing transport/store seams.
 * MAIN EXPORTS: ViewPreferenceConflictError, ViewPreferenceStateTooLargeError.
 * NON-RESPONSIBILITIES: Does not classify authentication or organisation errors.
 * NOTES FOR FUTURE AI: Only 409/412/428 responses with `current` are conflicts.
 */

import { VIEW_PREFERENCE_MAX_STATE_BYTES } from './contract'
import type {
  ViewPreferenceConflictStatus,
  ViewPreferenceEnvelope,
} from './contract'

export interface ViewPreferenceConflictErrorDetails {
  status?: ViewPreferenceConflictStatus
  code?: string
  message?: string
}

/** Conditional mutation rejection carrying the authoritative remote envelope. */
export class ViewPreferenceConflictError extends Error {
  // Explicit fields (not constructor parameter-properties) so the class
  // compiles under erasableSyntaxOnly. Tenant SPA tsc forbids param-properties.
  readonly envelope: ViewPreferenceEnvelope
  readonly status: ViewPreferenceConflictStatus
  readonly code: string

  constructor(
    envelope: ViewPreferenceEnvelope,
    details: ViewPreferenceConflictErrorDetails = {},
  ) {
    super(details.message ?? 'View preference changed on another client')
    this.name = 'ViewPreferenceConflictError'
    this.envelope = envelope
    this.status = details.status ?? 412
    this.code = details.code ?? 'revision_conflict'
  }
}

/** Client-side guard matching the backend's canonical 64 KiB state cap. */
export class ViewPreferenceStateTooLargeError extends Error {
  readonly byteSize: number

  constructor(byteSize: number) {
    super(
      `View preference state is ${byteSize} bytes; maximum is ${VIEW_PREFERENCE_MAX_STATE_BYTES}`,
    )
    this.name = 'ViewPreferenceStateTooLargeError'
    this.byteSize = byteSize
  }
}
